// 将历史接口和 SSE 的 V1/V2 差异收敛到同一 UI 模型，组件层不再判断后端协议版本。
/** 优先选择简体中文，否则返回第一条已配置语言文本。 */
function firstText(texts) {
  if (!Array.isArray(texts)) return '';
  const chinese = texts.find(item => item && item.languageCode === 'zh-CN');
  return String((chinese || texts[0] || {}).content || '');
}

function sortableTime(value) {
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function problemSortValue(problem) {
  return sortableTime(problem.latestEvidenceMessageTime || problem.latest_evidence_message_time);
}

function problemSortIndex(problem) {
  const index = problem.latestEvidenceMessageIndex != null
    ? problem.latestEvidenceMessageIndex
    : problem.latest_evidence_message_index;
  return Number.isFinite(Number(index)) ? Number(index) : -1;
}

function sortProblemsByEvidence(problems) {
  return problems.sort((left, right) => {
    const byIndex = problemSortIndex(right) - problemSortIndex(left);
    if (byIndex !== 0) return byIndex;
    const byTime = problemSortValue(right) - problemSortValue(left);
    if (byTime !== 0) return byTime;
    return (left.rankNo || 0) - (right.rankNo || 0);
  });
}

function answerKey(answer) {
  return String(answer && answer.value ? answer.value : '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function problemKey(problem) {
  return problem && problem.problemId != null ? `id:${problem.problemId}` : `name:${problem.problemName || ''}`;
}

function requestIdsOf(recommendation) {
  if (!recommendation) return [];
  const ids = Array.isArray(recommendation.requestIds) ? recommendation.requestIds.slice() : [];
  if (recommendation.requestId) ids.push(recommendation.requestId);
  return Array.from(new Set(ids.filter(Boolean)));
}

function withRequestIds(recommendation, requestIds) {
  return Object.assign({}, recommendation, { requestIds: Array.from(new Set((requestIds || []).filter(Boolean))) });
}

function hasRecommendationContent(recommendation) {
  return !!(recommendation && Array.isArray(recommendation.problems)
    && recommendation.problems.some(problem => problem && Array.isArray(problem.answers) && problem.answers.length));
}

function cloneProblemWithAnswers(problem, answers) {
  return Object.assign({}, problem, { answers: (answers || []).slice() });
}

function mergeProblemsByKey(problems) {
  const merged = [];
  const indexes = new Map();
  problems.forEach(problem => {
    if (!problem || !Array.isArray(problem.answers) || !problem.answers.length) return;
    const key = problemKey(problem);
    const index = indexes.get(key);
    if (index == null) {
      indexes.set(key, merged.length);
      merged.push(cloneProblemWithAnswers(problem, problem.answers));
      return;
    }
    merged[index] = cloneProblemWithAnswers(merged[index], merged[index].answers.concat(problem.answers));
  });
  return merged;
}

function dedupeAndLimitProblems(problems, maxAnswers) {
  const seenAnswers = new Set();
  let count = 0;
  const deduped = mergeProblemsByKey(problems).map(problem => {
    const answers = [];
    problem.answers.forEach(answer => {
      const key = answerKey(answer);
      if (!key || seenAnswers.has(key) || count >= maxAnswers) return;
      seenAnswers.add(key);
      answers.push(answer);
      count += 1;
    });
    return cloneProblemWithAnswers(problem, answers);
  }).filter(problem => problem.answers.length);
  return deduped;
}

function limitRecommendationAnswers(recommendation, maxAnswers) {
  if (!recommendation) return recommendation;
  const limit = Number.isFinite(Number(maxAnswers)) ? Number(maxAnswers) : 10;
  const sortedProblems = sortProblemsByEvidence((recommendation.problems || []).map(problem => cloneProblemWithAnswers(problem, problem.answers || [])));
  return Object.assign({}, recommendation, { problems: dedupeAndLimitProblems(sortedProblems, Math.max(limit, 0)) });
}

/** 将一期 program_answers 转换为统一问题卡片模型。 */
function legacyProblems(programAnswers, requestId) {
  // V1 没有稳定分类、游戏和敏感字段，显式补空值而不是根据文案猜测。
  return (Array.isArray(programAnswers) ? programAnswers : []).map((group, problemIndex) => ({
    problemId: group.problem_id || group.problemId || null,
    requestId,
    problemName: group.problem || group.problemName || '未命名问题',
    rankNo: problemIndex + 1,
    latestEvidenceMessageTime: group.latestEvidenceMessageTime || group.latest_evidence_message_time || '',
    latestEvidenceMessageId: group.latestEvidenceMessageId || group.latest_evidence_message_id || '',
    latestEvidenceMessageIndex: group.latestEvidenceMessageIndex != null
      ? group.latestEvidenceMessageIndex
      : group.latest_evidence_message_index,
    primaryCategory: null,
    secondaryCategory: null,
    sensitive: false,
    gameSpecific: false,
    games: [],
    answers: (Array.isArray(group.answer || group.answers) ? (group.answer || group.answers) : []).map((item, answerIndex) => ({
      answerId: item.id || item.answer_id || item.answerId || null,
      requestId,
      sortNo: item.sort_no || item.sortNo || answerIndex + 1,
      value: String(item.value || item.text || item.content || ''),
      canCopy: item.can_copy !== false && item.canCopy !== false,
      status: 'idle',
      clientId: `${requestId || 'legacy'}-${problemIndex}-${answerIndex}`,
    })).filter(item => item.value),
  }));
}

/** 将 V2 问题、游戏和多语言话术转换为有序 UI 模型。 */
function v2Problems(problems, requestId) {
  // 海外可能包含多语言文本；当前企微侧边栏优先展示简体中文，否则使用第一条已配置文本。
  const mapped = (Array.isArray(problems) ? problems : []).map((problem, problemIndex) => ({
    problemId: problem.problemId,
    requestId,
    problemName: problem.problemName || '未命名问题',
    rankNo: problem.rankNo || problemIndex + 1,
    latestEvidenceMessageTime: problem.latestEvidenceMessageTime || problem.latest_evidence_message_time || '',
    latestEvidenceMessageId: problem.latestEvidenceMessageId || problem.latest_evidence_message_id || '',
    latestEvidenceMessageIndex: problem.latestEvidenceMessageIndex != null
      ? problem.latestEvidenceMessageIndex
      : problem.latest_evidence_message_index,
    primaryCategory: problem.primaryCategory || null,
    secondaryCategory: problem.secondaryCategory || null,
    sensitive: problem.sensitive === true,
    gameSpecific: problem.gameSpecific === true,
    games: Array.isArray(problem.games) ? problem.games : [],
    answers: (Array.isArray(problem.answers) ? problem.answers : []).map((answer, answerIndex) => ({
      answerId: answer.answerId,
      requestId,
      sortNo: answer.sortNo || answerIndex + 1,
      value: firstText(answer.texts),
      canCopy: answer.canCopy !== false,
      status: 'idle',
      clientId: `${requestId || 'v2'}-${problem.problemId || problemIndex}-${answer.answerId || answerIndex}`,
    })).filter(item => item.value).sort((a, b) => a.sortNo - b.sortNo),
  }));
  return sortProblemsByEvidence(mapped);
}

/** 统一历史接口或 SSE 内容中的版本、状态和问题字段。 */
function normalizeRecommendation(content, meta) {
  const source = content && typeof content === 'object' ? content : {};
  const requestId = source.requestId || source.request_id || (meta && meta.requestId) || '';
  const v2 = Array.isArray(source.problems);
  return {
    schemaVersion: source.schemaVersion || source.schema_version || (v2 ? '2.0' : '1.0'),
    requestId,
    status: source.status || (v2 ? 'NO_HIT' : 'SUCCESS'),
    messageCursor: source.messageCursor || source.message_cursor || '',
    generatedAt: source.generatedAt || source.generated_at || (meta && meta.generatedAt) || '',
    replaceCurrent: source.replaceCurrent !== false && source.replace_current !== false,
    sensitive: source.sensitive === true || source.is_sensitive === true,
    subjectName: source.subjectName || source.subject_name || '',
    currentMainGameName: source.currentMainGameName || source.current_main_game_name || '',
    errorCode: source.errorCode || source.error_code || '',
    problems: v2 ? v2Problems(source.problems, requestId) : legacyProblems(
      source.programAnswers || source.program_answers || (meta && meta.programAnswers), requestId,
    ),
  };
}

/** 从 MQ/SSE 包装中优先提取 V2 完整结果，并兼容一期数组。 */
function parseJsonObject(value) {
  if (!value || typeof value !== 'string') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : value;
  } catch (_) {
    return value;
  }
}

function parseRecommendationObject(value) {
  const parsed = parseJsonObject(value);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
}

function extractRecommendationFromSsePayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const business = payload.businessMessage || payload.business_message || payload;
  const extra = parseJsonObject(business.extraData || business.extra_data) || {};
  // V2 完整结果位于 extraData.recommendation；没有该字段时才按 V1 program_answers 兼容解析。
  const recommendation = parseRecommendationObject(extra.recommendation);
  if (recommendation) return normalizeRecommendation(recommendation);
  const legacy = extra.program_answers || extra.programAnswers || business.program_answers || business.programAnswers
    || payload.program_answers || payload.programAnswers;
  if (!Array.isArray(legacy)) return null;
  return normalizeRecommendation({
    schema_version: extra.schema_version,
    request_id: extra.request_id || payload.requestId || payload.request_id,
    message_cursor: extra.message_cursor,
    generated_at: extra.generated_at || payload.sendTime || payload.send_time,
    replace_current: extra.replace_current,
    is_sensitive: extra.is_sensitive,
    status: extra.status,
    program_answers: legacy,
  });
}

/** 将生成时间转换为可比较毫秒值，非法时间按0处理。 */
function versionNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value);
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

/** 按请求 ID、生成时间和消息游标拒绝重复或迟到结果。 */
function shouldReplaceRecommendation(current, incoming) {
  if (!incoming) return false;
  if (!current || !current.requestId) return true;
  // 同 requestId 视为 MQ/SSE 重投，不重复渲染和上报曝光。
  if (incoming.requestId && incoming.requestId === current.requestId) return false;
  const currentTime = versionNumber(current.generatedAt);
  const incomingTime = versionNumber(incoming.generatedAt);
  if (incomingTime !== currentTime) return incomingTime > currentTime;
  // generatedAt 相同时用稳定消息游标兜底，阻止迟到的旧结果覆盖当前页面。
  return String(incoming.messageCursor || '') > String(current.messageCursor || '');
}

/** 追加新 SSE 结果：新命中排前、相同话术去重、总话术数受限。 */
function mergeRecommendation(current, incoming, maxAnswers) {
  if (!incoming) return current || null;
  if (!current || (!current.requestId && !hasRecommendationContent(current))) return limitRecommendationAnswers(withRequestIds(incoming, requestIdsOf(incoming)), maxAnswers);
  const currentRequestIds = requestIdsOf(current);
  if (incoming.requestId && currentRequestIds.includes(incoming.requestId)) return current;
  const currentTime = versionNumber(current.generatedAt);
  const incomingTime = versionNumber(incoming.generatedAt);
  if (incomingTime && currentTime && incomingTime < currentTime) return current;
  const hasVersionSignal = !!(incomingTime || currentTime || incoming.messageCursor || current.messageCursor);
  if (hasVersionSignal && incomingTime === currentTime && String(incoming.messageCursor || '') <= String(current.messageCursor || '')) return current;

  const incomingProblems = sortProblemsByEvidence((incoming.problems || []).map(problem => cloneProblemWithAnswers(problem, problem.answers || [])));
  const currentProblems = sortProblemsByEvidence((current.problems || []).map(problem => cloneProblemWithAnswers(problem, problem.answers || [])));
  const problems = dedupeAndLimitProblems(incomingProblems.concat(currentProblems), Math.max(Number(maxAnswers) || 10, 0));
  return withRequestIds(Object.assign({}, current, incoming, {
    sensitive: current.sensitive === true || incoming.sensitive === true,
    problems,
  }), currentRequestIds.concat(incoming.requestId || []));
}

/** 在不改变原始排序和对象的前提下执行本地问题/话术搜索。 */
function filterRecommendation(recommendation, keyword) {
  const text = String(keyword || '').trim().slice(0, 50).toLowerCase();
  if (!recommendation || !text) return recommendation;
  // 本地过滤只改变可见集合，不排序也不修改原 recommendation，清空关键字即可恢复完整结果。
  return Object.assign({}, recommendation, {
    problems: recommendation.problems.map(problem => {
      const problemMatch = problem.problemName.toLowerCase().includes(text);
      const answers = problemMatch ? problem.answers : problem.answers.filter(answer => answer.value.toLowerCase().includes(text));
      return Object.assign({}, problem, { answers });
    }).filter(problem => problem.problemName.toLowerCase().includes(text) || problem.answers.length),
  });
}

/** 生成当前页面用于曝光去重的稳定复合键。 */
function exposureKey(requestId, eventType, problemId, answerId) {
  // 前端去重只控制当前页面重复渲染；服务端仍用 event_id 和页面会话去重作最终保障。
  return [requestId || '', eventType || '', problemId || '', answerId || ''].join(':');
}

/**
 * 优先使用服务端签发 access token 时绑定的 SSE 路由 userId；旧接口回退原玩家 externalUserId。
 */
function resolveSseUserId(historyContent, externalUserId) {
  const content = historyContent && typeof historyContent === 'object' ? historyContent : {};
  return String(content.sse_user_id || content.sseUserId || externalUserId || '');
}

// 浏览器构建统一使用 ES Module，避免 Webpack Harmony 包装器中的只读 exports 被 CommonJS 重写。
export {
  normalizeRecommendation,
  extractRecommendationFromSsePayload,
  shouldReplaceRecommendation,
  mergeRecommendation,
  limitRecommendationAnswers,
  filterRecommendation,
  exposureKey,
  resolveSseUserId,
};
